// src/views/Admin/AdminPortal.tsx
import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { 
  HiOutlineBuildingOffice2,
  HiOutlineBuildingOffice,
  HiOutlineUsers,
  HiOutlineShieldCheck,
  HiOutlineCog6Tooth,
  HiOutlineSquares2X2,
  HiChevronRight,
  HiArrowLeftOnRectangle,
  HiMiniBuildingStorefront
} from 'react-icons/hi2';
import './AdminPortal.scss';

// Admin Views
import AdminHome from './Home/Home';
import Locales from './Locales/Locales';
import LocalPage from './Locales/LocalPage/LocalPage';
import Permissions from './Permissions/Permissions';
import Companies from './Companies/Companies';
import LocalPlans from './LocalPlans/LocalPlans';

interface MenuItem {
  name: string;
  route: string;
  icon: React.ElementType;
}

const menuItems: MenuItem[] = [
  { name: 'Dashboard', route: '/MedioPuntoAdmin', icon: HiOutlineSquares2X2 },
  { name: 'Locales', route: '/MedioPuntoAdmin/Locales', icon: HiOutlineBuildingOffice2 },
  { name: 'Planes Locales', route: '/MedioPuntoAdmin/LocalPlans', icon: HiMiniBuildingStorefront },
  { name: 'Compañías', route: '/MedioPuntoAdmin/Companias', icon: HiOutlineBuildingOffice },
  { name: 'Usuarios', route: '/MedioPuntoAdmin/Usuarios', icon: HiOutlineUsers },
  { name: 'Permisos', route: '/MedioPuntoAdmin/Permisos', icon: HiOutlineShieldCheck },
  { name: 'Configuración', route: '/MedioPuntoAdmin/Configuracion', icon: HiOutlineCog6Tooth },
  
];

const AdminPortal: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState<string>('Dashboard');

  useEffect(() => {
    // Update active item based on current route
    const currentItem = menuItems.find(item => 
      location.pathname === item.route || 
      (item.route !== '/MedioPuntoAdmin' && location.pathname.startsWith(item.route))
    );
    if (currentItem) {
      setActiveItem(currentItem.name);
    } else if (location.pathname === '/MedioPuntoAdmin') {
      setActiveItem('Dashboard');
    }
  }, [location.pathname]);

  const handleMenuClick = (item: MenuItem) => {
    setActiveItem(item.name);
    navigate(item.route);
  };

  const handleLogout = () => {
    navigate('/App');
  };

  return (
    <div className="admin-portal">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <div className="admin-logo">
            <span className="logo-icon">MP</span>
            <span className="logo-text">Admin Portal</span>
          </div>
        </div>

        <nav className="admin-nav">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeItem === item.name;
            return (
              <button
                key={item.name}
                className={`admin-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => handleMenuClick(item)}
              >
                <Icon className="nav-icon" size={20} />
                <span className="nav-text">{item.name}</span>
                {isActive && <HiChevronRight className="nav-chevron" size={16} />}
              </button>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <button className="admin-logout-btn" onClick={handleLogout}>
            <HiArrowLeftOnRectangle size={20} />
            <span>Volver a la App</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <Routes>
          <Route path="/" element={<AdminHome />} />
          <Route path="/Locales" element={<Locales />} />
          <Route path="/Locales/:localId" element={<LocalPage />} />
          <Route path="/Companias" element={<Companies />} />
          <Route path="/Usuarios" element={<PlaceholderView title="Usuarios" />} />
          <Route path="/Permisos" element={<Permissions />} />
          <Route path="/Configuracion" element={<PlaceholderView title="Configuración" />} />
          <Route path="/LocalPlans" element={<LocalPlans />} />
        </Routes>
      </main>
    </div>
  );
};

// Placeholder component for sections not yet implemented
const PlaceholderView: React.FC<{ title: string }> = ({ title }) => (
  <div className="admin-placeholder">
    <h2>{title}</h2>
    <p>Esta sección estará disponible próximamente.</p>
  </div>
);

export default AdminPortal;
